import { useState, useEffect, useCallback } from 'react';
import {
  ResourceItem, getResources, createResource, updateResource,
  deleteResource, uploadResourceImage,
} from './api';

type Section = 'info' | 'tuto' | 'concept' | 'agent_guide';

const SECTION_LABEL: Record<Section, string> = {
  info: 'Infos',
  tuto: 'Tutos',
  concept: 'Concepts',
  agent_guide: 'Guides agents',
};
const SECTION_ICON: Record<Section, string> = {
  info: 'ℹ️',
  tuto: '🎓',
  concept: '💡',
  agent_guide: '🛡️',
};

interface Props {
  isAdmin: boolean;
  canSeeAgentGuides: boolean;
  isMobile?: boolean;
}

const COLOR_MAP: Record<string, string> = {
  blue: '#2563eb', green: '#16a34a', purple: '#7c3aed',
  red: '#dc2626', orange: '#ea580c', pink: '#db2777',
  teal: '#0d9488', yellow: '#ca8a04', cyan: '#0891b2',
  gold: '#d4af37', indigo: '#4f46e5', violet: '#7c3aed',
};

type ViewState =
  | { t: 'list' }
  | { t: 'detail'; item: ResourceItem }
  | { t: 'edit'; item: ResourceItem | null };

export default function Ressources({ isAdmin, canSeeAgentGuides, isMobile }: Props) {
  const tabs: Section[] = canSeeAgentGuides
    ? ['info', 'tuto', 'concept', 'agent_guide']
    : ['info', 'tuto', 'concept'];
  const [tab, setTab] = useState<Section>('info');
  const [allItems, setAllItems] = useState<Record<Section, ResourceItem[]>>({ info: [], tuto: [], concept: [], agent_guide: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewState>({ t: 'list' });

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all(tabs.map(s => getResources(s).then(r => [s, r.data] as [Section, ResourceItem[]]).catch(() => [s, []] as [Section, ResourceItem[]])))
      .then(results => {
        const next: Record<Section, ResourceItem[]> = { info: [], tuto: [], concept: [], agent_guide: [] };
        results.forEach(([s, list]) => { next[s] = list; });
        setAllItems(next);
      })
      .finally(() => setLoading(false));
  }, [canSeeAgentGuides]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  const items = allItems[tab] || [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter(it => (it.title || '').toLowerCase().includes(q) || (it.body || '').toLowerCase().includes(q))
    : items;

  const goList = () => setView({ t: 'list' });
  const refreshList = () => { goList(); loadAll(); };

  // Sur mobile : si une vue détail/édition est active, on rend en plein écran
  // (pas de colonne gauche visible) ; sinon on affiche la colonne gauche.
  const showLeft = !isMobile || view.t === 'list';
  const showRight = !isMobile || view.t !== 'list';

  const leftPanel = (
    <div style={{ width: isMobile ? '100%' : 320, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : '1px solid #e2e8f0', height: '100%', background: '#f8fafc' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un article…"
          style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc', boxSizing: 'border-box', outline: 'none' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {tabs.map(s => {
            const n = (allItems[s] || []).length;
            const active = tab === s;
            return (
              <button key={s} onClick={() => setTab(s)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '8px 6px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  background: active ? '#7c3aed' : '#f1f5f9',
                  color: active ? '#fff' : '#475569' }}>
                <span style={{ fontSize: 14 }}>{SECTION_ICON[s]}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{SECTION_LABEL[s]}</span>
                <span style={{ background: active ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                  color: active ? '#fff' : '#64748b', borderRadius: 999, fontSize: 10, fontWeight: 700,
                  padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, fontSize: 13 }}>Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 28, fontSize: 13 }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>📄</div>
            {q ? 'Aucun résultat' : `Aucun article dans « ${SECTION_LABEL[tab]} »`}
          </div>
        )}
        {filtered.map(it => {
          const accent = it.extra?.color_theme ? (COLOR_MAP[it.extra.color_theme] || '#7c3aed') : '#7c3aed';
          const selected = view.t === 'detail' && view.item.id === it.id;
          return (
            <div key={it.id} onClick={() => setView({ t: 'detail', item: it })}
              style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                border: selected ? `2px solid ${accent}` : '1px solid #e2e8f0',
                boxShadow: selected ? `0 0 0 3px ${accent}22` : '0 1px 2px rgba(0,0,0,0.04)',
                display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              {it.image_url ? (
                <img src={it.image_url} alt="" loading="lazy"
                  style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block', background: '#f1f5f9' }} />
              ) : (
                <div style={{ width: '100%', height: 90, background: `${accent}1f`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38 }}>
                  {it.extra?.icon || '📄'}
                </div>
              )}
              <div style={{ padding: '10px 12px' }}>
                <div style={{ color: '#0f172a', fontSize: 13, fontWeight: 700, lineHeight: 1.35, wordBreak: 'break-word' }}>
                  {it.extra?.icon ? `${it.extra.icon} ` : ''}{it.title}
                </div>
                {it.body && (
                  <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4, lineHeight: 1.4,
                    overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {it.body}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
          <button onClick={() => setView({ t: 'edit', item: null })}
            style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + Nouvel article
          </button>
        </div>
      )}
    </div>
  );

  const rightPanel = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc', minWidth: 0 }}>
      {view.t === 'detail' && (
        <DetailView
          item={view.item}
          isAdmin={isAdmin}
          isMobile={isMobile}
          onBack={goList}
          onEdit={() => setView({ t: 'edit', item: view.item })}
          onDeleted={refreshList}
        />
      )}
      {view.t === 'edit' && (
        <EditView
          item={view.item}
          defaultSection={tab}
          isMobile={isMobile}
          onBack={goList}
          onSaved={refreshList}
        />
      )}
      {view.t === 'list' && !isMobile && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
            <div style={{ fontSize: 14 }}>Sélectionne un article à gauche</div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      <div style={{ flexShrink: 0, background: '#fff', borderBottom: '1px solid #e2e8f0',
        padding: isMobile ? '12px 14px' : '18px 28px' }}>
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? 17 : 20, fontWeight: 700 }}>
          📚 Mes ressources
        </h1>
        {!isMobile && (
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
            Infos, tutos, concepts et guides agents
          </p>
        )}
      </div>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {showLeft && leftPanel}
        {showRight && rightPanel}
      </div>
    </div>
  );
}

/* ── Barre de navigation haute (retour) ─────────────────── */
function TopBar({ title, onBack, isMobile, right }: {
  title: string; onBack: () => void; isMobile?: boolean; right?: React.ReactNode;
}) {
  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0,
      padding: isMobile ? '12px 14px' : '16px 32px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>
        ← Retour
      </button>
      <div style={{ color: '#0f172a', fontSize: isMobile ? 15 : 18, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title}
      </div>
      {right}
    </div>
  );
}

/* ── Vue détail (pleine page) ────────────────────────────── */
function DetailView({ item, isAdmin, isMobile, onBack, onEdit, onDeleted }: {
  item: ResourceItem; isAdmin: boolean; isMobile?: boolean;
  onBack: () => void; onEdit: () => void; onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const ex = item.extra;
  const accent = ex?.color_theme ? (COLOR_MAP[ex.color_theme] || '#7c3aed') : '#7c3aed';

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cet article ?')) return;
    setDeleting(true);
    try { await deleteResource(item.id); onDeleted(); }
    catch { setDeleting(false); }
  };

  const block = (label: string, arr?: string[] | null) =>
    arr && arr.length > 0 ? (
      <div style={{ marginTop: 18 }}>
        <div style={{ color: accent, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
        <ul style={{ margin: 0, paddingLeft: 20, color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
          {arr.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </div>
    ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      <TopBar title={item.title} onBack={onBack} isMobile={isMobile}
        right={isAdmin ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onEdit} style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Modifier</button>
            <button onClick={handleDelete} disabled={deleting} style={{ background: 'none', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '7px 14px', cursor: deleting ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {deleting ? '…' : 'Supprimer'}
            </button>
          </div>
        ) : undefined}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? 0 : '28px 32px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', borderRadius: isMobile ? 0 : 16, overflow: 'hidden', border: isMobile ? 'none' : '1px solid #e2e8f0' }}>
          {item.image_url && (
            <img src={item.image_url} alt="" style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }} />
          )}
          <div style={{ padding: isMobile ? 18 : 28 }}>
            <h1 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? 19 : 24, fontWeight: 700, lineHeight: 1.3 }}>
              {ex?.icon ? `${ex.icon} ` : ''}{item.title}
            </h1>

            {ex?.objective && (
              <div style={{ marginTop: 16, background: `${accent}10`, borderLeft: `3px solid ${accent}`, padding: '10px 14px', borderRadius: 6, color: '#334155', fontSize: 14 }}>
                <strong>🎯 Objectif :</strong> {ex.objective}
              </div>
            )}
            {ex?.frequency && (
              <div style={{ marginTop: 8, color: '#64748b', fontSize: 13 }}>
                <strong>Fréquence :</strong> {ex.frequency}
              </div>
            )}

            {item.body && (
              <div style={{ marginTop: 16, color: '#334155', fontSize: 15, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                {item.body}
              </div>
            )}

            {block('Étapes', ex?.steps)}
            {block('Bonnes pratiques', ex?.best_practices)}
            {block('Conseils', ex?.tips)}
            {block('À éviter', ex?.avoid)}

            {ex?.example_message && (
              <div style={{ marginTop: 18 }}>
                <div style={{ color: accent, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Message type</div>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', color: '#334155', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                  {ex.example_message}
                </div>
              </div>
            )}

            {ex?.images && ex.images.length > 1 && (
              <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px,1fr))', gap: 8 }}>
                {ex.images.slice(1).map((u, i) => (
                  <img key={i} src={u} alt="" style={{ width: '100%', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                ))}
              </div>
            )}

            {ex?.tags && ex.tags.length > 0 && (
              <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ex.tags.map((t, i) => (
                  <span key={i} style={{ background: '#f1f5f9', color: '#475569', fontSize: 12, padding: '3px 10px', borderRadius: 999 }}>#{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Champs réutilisables ────────────────────────────────── */
const FIELD: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8,
  border: '1px solid #cbd5e1', fontSize: 14, marginBottom: 16,
};
const LBL: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4,
};
const EMOJI_CHOICES = ['💬', '👥', '🎮', '🎵', '🎬', '🎨', '🏆', '🎁', '❤️', '✨', '⚡', '📖', '🎓', '🛒', '🍴', '📻', '🗺️', '🏋️', '🏅', '❓', '🔊', '💡'];

function ListEditor({ label, items, onChange }: {
  label: string; items: string[]; onChange: (v: string[]) => void;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={LBL}>{label}</label>
      {items.map((val, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input value={val}
            onChange={e => { const n = [...items]; n[i] = e.target.value; onChange(n); }}
            style={{ flex: 1, boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} />
          <button onClick={() => onChange(items.filter((_, j) => j !== i))}
            style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, width: 40, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>×</button>
        </div>
      ))}
      <button onClick={() => onChange([...items, ''])}
        style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '2px 0' }}>
        + Ajouter
      </button>
    </div>
  );
}

/* ── Vue création / édition (pleine page) ────────────────── */
function EditView({ item, defaultSection, isMobile, onBack, onSaved }: {
  item: ResourceItem | null; defaultSection: Section; isMobile?: boolean;
  onBack: () => void; onSaved: () => void;
}) {
  const ex0 = item?.extra || {};
  const [section, setSection] = useState<Section>((item?.section as Section) || defaultSection);
  const [title, setTitle] = useState(item?.title || '');
  const [body, setBody] = useState(item?.body || '');
  const [objective, setObjective] = useState(ex0.objective || '');
  const [frequency, setFrequency] = useState(ex0.frequency || '');
  const [icon, setIcon] = useState(ex0.icon || '');
  const [color, setColor] = useState(ex0.color_theme || 'blue');
  const [bestPractices, setBestPractices] = useState<string[]>(ex0.best_practices || []);
  const [steps, setSteps] = useState<string[]>(ex0.steps || []);
  const [tips, setTips] = useState<string[]>(ex0.tips || []);
  const [avoid, setAvoid] = useState<string[]>(ex0.avoid || []);
  const [tags, setTags] = useState<string[]>(ex0.tags || []);
  const [exampleMsg, setExampleMsg] = useState(ex0.example_message || '');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const isConcept = section === 'concept';
  const isGuide = section === 'agent_guide';
  const isRich = isConcept || isGuide;

  const buildExtra = () => {
    const clean = (a: string[]) => a.map(s => s.trim()).filter(Boolean);
    if (isConcept) {
      return {
        icon, color_theme: color, objective,
        best_practices: clean(bestPractices),
        avoid: clean(avoid),
        tips: clean(tips),
      };
    }
    if (isGuide) {
      return {
        icon, color_theme: color, objective, frequency,
        steps: clean(steps), tips: clean(tips), avoid: clean(avoid),
        tags: clean(tags), example_message: exampleMsg,
        images: ex0.images || [],
      };
    }
    return null;
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const extra = buildExtra();
      const payload: any = { section, title: title.trim(), body };
      if (isRich) payload.extra_json = JSON.stringify(extra);
      let id = item?.id;
      if (item) {
        await updateResource(item.id, payload);
      } else {
        const r = await createResource(payload);
        id = r.data.id;
      }
      if (file && id) await uploadResourceImage(id, file);
      onSaved();
    } catch { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      <TopBar title={item ? 'Modifier l’article' : 'Nouvel article'} onBack={onBack} isMobile={isMobile}
        right={
          <button onClick={handleSave} disabled={saving || !title.trim()}
            style={{ background: saving || !title.trim() ? '#c4b5fd' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: saving || !title.trim() ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        }
      />
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? 16 : '28px 32px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: isMobile ? 18 : 28 }}>
          <label style={LBL}>Section</label>
          <select value={section} onChange={e => setSection(e.target.value as Section)} style={FIELD}>
            <option value="info">Infos</option>
            <option value="tuto">Tutos</option>
            <option value="concept">Concepts</option>
            <option value="agent_guide">Guides agents</option>
          </select>

          <label style={LBL}>Titre</label>
          <input value={title} onChange={e => setTitle(e.target.value)} style={FIELD} />

          <label style={LBL}>{isRich ? 'Description' : 'Contenu'}</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={isRich ? 5 : 12}
            style={{ ...FIELD, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />

          {isRich && (
            <>
              <label style={LBL}>Objectif</label>
              <input value={objective} onChange={e => setObjective(e.target.value)} style={FIELD} />

              {isGuide && (
                <>
                  <label style={LBL}>Fréquence</label>
                  <input value={frequency} onChange={e => setFrequency(e.target.value)} style={FIELD} />
                </>
              )}

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={LBL}>Icône</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 26, width: 38, textAlign: 'center' }}>{icon || '—'}</span>
                    <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="emoji"
                      style={{ flex: 1, boxSizing: 'border-box', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
                    {EMOJI_CHOICES.map(e => (
                      <button key={e} onClick={() => setIcon(e)}
                        style={{ fontSize: 18, width: 34, height: 34, borderRadius: 7, cursor: 'pointer',
                          border: `1px solid ${icon === e ? '#7c3aed' : '#e2e8f0'}`,
                          background: icon === e ? '#f5f3ff' : '#fff' }}>{e}</button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={LBL}>Couleur</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: COLOR_MAP[color] || '#7c3aed' }} />
                    <select value={color} onChange={e => setColor(e.target.value)}
                      style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, marginBottom: 16 }}>
                      {Object.keys(COLOR_MAP).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {isConcept && (
                <>
                  <ListEditor label="Bonnes pratiques" items={bestPractices} onChange={setBestPractices} />
                  <ListEditor label="Erreurs à éviter" items={avoid} onChange={setAvoid} />
                  <ListEditor label="Conseils pratiques" items={tips} onChange={setTips} />
                </>
              )}

              {isGuide && (
                <>
                  <ListEditor label="Étapes" items={steps} onChange={setSteps} />
                  <ListEditor label="Conseils" items={tips} onChange={setTips} />
                  <ListEditor label="À éviter" items={avoid} onChange={setAvoid} />
                  <label style={LBL}>Message type</label>
                  <textarea value={exampleMsg} onChange={e => setExampleMsg(e.target.value)} rows={4}
                    style={{ ...FIELD, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                  <ListEditor label="Tags" items={tags} onChange={setTags} />
                </>
              )}
            </>
          )}

          <label style={LBL}>Image {item?.image_url && '(remplacer)'}</label>
          {item?.image_url && !file && (
            <img src={item.image_url} alt="" style={{ maxWidth: 200, borderRadius: 8, border: '1px solid #e2e8f0', display: 'block', marginBottom: 8 }} />
          )}
          <input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)}
            style={{ fontSize: 13 }} />
        </div>
      </div>
    </div>
  );
}
